import { useRef } from 'react'

import { useFrame } from '../../../engine'
import { anim, clamp, seg, smooth } from '../../../motion'
import { useCopy } from '../../i18n'
import codeknacker from '../../showcase/media/game-codeknacker.mp4'
import { Odometer, ScrollVideo } from '../../showcase/stage/fx'
import { C, OUTLINES, morphPolygon } from '../shapes'

// Drei Fenster, dazwischen morpht die Form
const WIN = [
  [0, 0.34],
  [0.34, 0.67],
  [0.67, 1]
]
const MORPH = [
  [0.29, 0.39],
  [0.62, 0.72]
]
const ORDER = ['tri', 'square', 'circle']
const COLORS = [C.yellow, C.green, C.pink]

// Einschätzungen der drei Rollen, daraus wird der Zielbereich
const EXPERTS = [
  { at: 58, color: C.blue },
  { at: 74, color: C.ink },
  { at: 66, color: '#FFFFFF' }
]

const hex = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16))
const mix = (a, b, t) => {
  const A = hex(a)
  const B = hex(b)
  return `rgb(${A.map((v, i) => Math.round(v + (B[i] - v) * t)).join(',')})`
}

/**
 * Signature-Moment: eine große Aivy-Form, die beim Scrollen vom Dreieck zum
 * Quadrat zum Kreis wird. In ihr steckt jeweils der Schritt, links die Liste.
 */
export default function PlSteps() {
  const c = useCopy('schritte')
  const ui = useCopy('ui')
  const shapeRef = useRef(null)

  useFrame((l) => {
    const el = shapeRef.current
    if (!el) return
    let from = 0
    let t = 0
    if (l >= MORPH[1][0]) {
      from = 1
      t = seg(l, ...MORPH[1])
    } else if (l >= MORPH[0][0]) {
      t = seg(l, ...MORPH[0])
    }
    const e = smooth(t)
    const to = Math.min(2, from + 1)
    // Beim Morph dreht die Form kurz mit und springt leicht
    const rot = Math.sin(e * Math.PI) * 0.5
    const pop = 1 - Math.sin(e * Math.PI) * 0.08
    el.style.clipPath = morphPolygon(
      OUTLINES[ORDER[from]],
      OUTLINES[ORDER[to]],
      e,
      rot
    )
    el.style.backgroundColor = mix(COLORS[from], COLORS[to], e)
    el.style.transform = `scale(${pop.toFixed(3)})`
    el.style.setProperty('--exp', clamp(seg(l, 0.08, 0.24)).toFixed(3))
  })

  return (
    <div className='ap-steps'>
      <div className='ap-steps__copy'>
        <div className='ap-eyebrow'>{c.eyebrow}</div>
        <h2 className='ap-h1'>{c.title}</h2>
        <ol className='ap-steps__list'>
          {c.steps.map((s, i) => (
            <li
              key={s.title}
              className='ap-steps__item'
              style={{
                // 1 im eigenen Fenster, der erste Schritt steht schon zu Beginn
                '--on': `clamp(0, min((var(--l) - ${WIN[i][0]}) * 30 + ${i === 0 ? 1 : 0}, (${WIN[i][1]} - var(--l)) * 30 + ${i === 2 ? 1 : 0}), 1)`
              }}
            >
              <span className='ap-steps__num'>{`0${i + 1}`}</span>
              <span className='ap-steps__body'>
                <span className='ap-steps__title'>{s.title}</span>
                <span className='ap-steps__text'>{s.text}</span>
              </span>
            </li>
          ))}
        </ol>
      </div>

      <div className='ap-steps__stage'>
        <div ref={shapeRef} className='ap-steps__shape'>
          <div
            className='ap-steps__layer ap-steps__layer--tri'
            style={anim({ at: [0.06, 0.14], out: [0.28, 0.32], y: 30 })}
          >
            {c.experts.map((name, i) => (
              <div key={name} className='ap-steps__expert'>
                <span>{name}</span>
                <span className='ap-steps__track'>
                  <i
                    style={{
                      left: `calc(${EXPERTS[i].at}% * var(--exp, 0))`,
                      background: EXPERTS[i].color
                    }}
                  />
                </span>
              </div>
            ))}
            <div
              className='ap-steps__target'
              style={anim({ at: [0.18, 0.24], y: 12 })}
            >
              <span>{c.target}</span>
              <span className='ap-steps__chip'>{c.minutes}</span>
            </div>
          </div>

          <div
            className='ap-steps__layer ap-steps__layer--video'
            style={anim({ at: [0.37, 0.42], out: [0.6, 0.64], y: 0, s: 1.1 })}
          >
            <ScrollVideo
              src={codeknacker}
              at={[0.34, 0.66]}
              className='ap-steps__video'
              label='Codeknacker'
            />
          </div>

          <div
            className='ap-steps__layer ap-steps__layer--score'
            style={anim({ at: [0.72, 0.8], y: 24 })}
          >
            <span className='ap-steps__score-label'>{c.score}</span>
            <Odometer
              value='82'
              at={[0.73, 0.86]}
              className='ap-steps__score'
            />
            <span className='ap-steps__fit'>{c.fit}</span>
            <span className='ap-steps__sample'>{ui.sample}</span>
          </div>
        </div>
      </div>
    </div>
  )
}
